import { Listbox, Transition } from '@headlessui/react';
import { bem } from '@socialive/common';
import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import { usePopper } from 'react-popper';
import { SelectDownIcon } from '../../Icons';
import Portal from '../Portal';
import SelectOption from '../SelectOption';
import './select.css';

/*
#tailwind
select__button select__button--small select__button--medium select__button--large select__button--error select__button--warning
select__button--disabled select__button--activated
*/

const Select = ({ children, context, options, value, disabled, onChange, name, size, ...other }) => {
  const [ref, setRef] = useState();
  const [popperElement, setPopperElement] = useState();

  const modifiers = useMemo(
    () => [
      {
        name: 'flip',
        options: {
          allowedAutoPlacements: ['bottom-start'],
        },
      },
      {
        name: 'sameWidth',
        enabled: true,
        fn: ({ state }) => {
          // eslint-disable-next-line no-param-reassign
          state.styles.popper.width = `${state.rects.reference.width}px`;
        },
        phase: 'beforeWrite',
        requires: ['computeStyles'],
      },
    ],
    []
  );

  const { styles } = usePopper(ref, popperElement, {
    placement: 'bottom-start',
    modifiers,
  });

  const valueLabel = useMemo(
    () =>
      options?.find((o) => o.value === value)?.label ?? children?.find((c) => c.props.value === value)?.props?.children,
    [options, value, children]
  );

  return (
    <div {...other}>
      <Listbox value={value} onChange={onChange} disabled={disabled} name={name}>
        {({ open }) => (
          <div className="select__innerContainer">
            <Listbox.Button
              ref={setRef}
              className={bem`select__button--${{
                activated: open,
                disabled,
                [context]: true,
                [size]: true,
              }}`}
            >
              <span className="select__currentValue">{valueLabel}</span>
              <span
                className={bem`select__buttonIcon--${{
                  rotate: open,
                }}`}
              >
                <SelectDownIcon aria-hidden="true" />
              </span>
            </Listbox.Button>
            <Transition>
              <Portal id="socialive-ui-atoms-select">
                <Listbox.Options
                  className="select__options"
                  ref={setPopperElement}
                  style={{ ...styles.popper, zIndex: 10001, overflow: 'hidden' }}
                >
                  {options?.map((option, index) => (
                    <SelectOption
                      key={option.id ?? index}
                      active={option.active}
                      selected={option.selected}
                      value={option?.value ?? option}
                      disabled={option.disabled}
                    >
                      {option?.label ?? option}
                    </SelectOption>
                  )) ?? children}
                </Listbox.Options>
              </Portal>
            </Transition>
          </div>
        )}
      </Listbox>
    </div>
  );
};

Select.propTypes = {
  children: PropTypes.node,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      value: PropTypes.PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.string,
      disabled: PropTypes.bool,
    })
  ),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  context: PropTypes.oneOf(['error', 'warning', 'default']),
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
  name: PropTypes.string,
};

Select.defaultProps = {
  children: undefined,
  options: [],
  value: undefined,
  context: 'default',
  size: 'medium',
  disabled: false,
  onChange: undefined,
  name: undefined,
};

export default Select;
